import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SectionTitle } from "@/components/dashboard/widgets";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, ExternalLink, Copy, Eye, MousePointerClick, PoundSterling, MoreVertical, Loader2, Trash2, Pencil, Sparkles } from "lucide-react";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

/**
 * Publisher Landers (Pack E)
 *
 * Spin up sector-specific landing pages in one click. Each lander has its own
 * unique `/l/<slug>` URL, captures sub_id from the query string, and submissions
 * flow straight into the production ingest pipeline (wallet debit, dedup,
 * publisher earnings credit).
 */
export default function Landers() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [tplId, setTplId] = useState("");
  const [slug, setSlug] = useState("");
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      setLoading(true);
      const [r1, r2] = await Promise.all([
        api.get("/publisher/landers"),
        api.get("/publisher/landers/templates"),
      ]);
      setRows(r1.data || []);
      setTemplates(r2.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (!tplId) { toast.error("Pick a template first"); return; }
    setCreating(true);
    try {
      const res = await api.post("/publisher/landers", { template_id: tplId, slug: slug || undefined });
      toast.success("Lander created");
      setOpen(false); setTplId(""); setSlug("");
      nav(`/publisher/landers/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create lander");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete lander "${l.slug}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/publisher/landers/${l.id}`);
      toast.success("Lander deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const landerUrl = (l) => `${BACKEND_URL}/l/${l.slug}`;

  return (
    <div className="space-y-6">
      <SectionTitle
        eyebrow="Publisher landers"
        title="Hosted landing pages"
        subtitle="Spin up sector-branded landers in one click. Every submission flows straight into the ingest pipeline — wallet debit, dedup and earnings, all automatic."
        actions={
          <Button className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white" onClick={() => setOpen(true)} data-testid="lander-new-cta">
            <Plus className="size-4" /> New lander
          </Button>
        }
      />

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="size-5 text-slate-400 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <EmptyState onClick={() => setOpen(true)} />
      ) : (
        <div className="grid gap-4">
          {rows.map((l) => (
            <Card key={l.id} className="rounded-xl border-slate-200 shadow-soft" data-testid={`lander-row-${l.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900 truncate">{l.headline}</h3>
                      <span
                        className="inline-flex items-center text-[10.5px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-md"
                        style={{ background: l.active === false ? "#fef2f2" : "#ecfdf5", color: l.active === false ? "#b91c1c" : "#047857" }}
                      >
                        {l.active === false ? "Paused" : "Live"}
                      </span>
                      <span className="text-[11px] uppercase tracking-widest text-slate-500">{(l.sector || "").replace("-", " ")}</span>
                    </div>
                    <div className="mt-1.5 text-[12.5px] text-slate-500 truncate font-mono">
                      <span>/l/{l.slug}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => copyToClipboard(landerUrl(l), "URL copied")} data-testid={`lander-copy-${l.id}`}>
                      <Copy className="size-3.5" /> Copy URL
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-full" asChild>
                      <a href={landerUrl(l)} target="_blank" rel="noreferrer" data-testid={`lander-open-${l.id}`}>
                        <ExternalLink className="size-3.5" /> Open
                      </a>
                    </Button>
                    <Button size="sm" className="rounded-full bg-slate-900 text-white hover:bg-slate-700" onClick={() => nav(`/publisher/landers/${l.id}`)} data-testid={`lander-edit-${l.id}`}>
                      <Pencil className="size-3.5" /> Edit
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="rounded-full" data-testid={`lander-more-${l.id}`}><MoreVertical className="size-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => remove(l)} className="text-rose-600 focus:text-rose-700">
                          <Trash2 className="size-3.5 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                {/* Mini KPIs */}
                <div className="mt-5 grid grid-cols-3 gap-3">
                  <Mini label="Views"        value={(l.views || 0).toLocaleString()}        Icon={Eye} />
                  <Mini label="Submissions"  value={(l.submissions || 0).toLocaleString()}  Icon={MousePointerClick} />
                  <Mini label="Conv. rate"   value={l.views ? `${((l.submissions / l.views) * 100).toFixed(1)}%` : "—"} Icon={PoundSterling} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="lander-create-dialog" className="max-w-xl p-0 overflow-hidden gap-0 border-slate-200">
          {/* Navy header strip — premium signal */}
          <div className="bg-[#0F172A] text-white px-6 py-5">
            <DialogHeader className="space-y-1">
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">Publisher · Landers</div>
              <DialogTitle className="text-[19px] font-semibold text-white tracking-tight">New lander</DialogTitle>
              <DialogDescription className="text-[12.5px] text-slate-300">
                Pick a Connexis-curated template — your lander goes live on a hosted page with the submission pipeline wired in.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="px-6 py-5 space-y-5">
            <div className="grid grid-cols-1 gap-2 max-h-[320px] overflow-y-auto pr-1 -mr-1">
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTplId(t.id)}
                  className={`text-left rounded-md border px-3.5 py-3 transition-all ${tplId === t.id ? "border-[#0F172A] shadow-[0_0_0_1px_#0F172A]" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`lander-tpl-${t.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-slate-900 text-[13.5px]">{t.name}</div>
                    <div className="text-[9.5px] uppercase tracking-[0.12em] text-slate-500 font-semibold">{(t.sector || "").replace("-", " ")}</div>
                  </div>
                  <div className="text-[12px] text-slate-500 mt-1 line-clamp-2 leading-relaxed">{t.subheadline}</div>
                </button>
              ))}
            </div>

            <div className="space-y-1.5 pt-1">
              <Label htmlFor="lander-slug" className="text-[11.5px] uppercase tracking-[0.1em] font-semibold text-slate-600">Slug (optional)</Label>
              <Input
                id="lander-slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto-generated"
                data-testid="lander-slug-input"
                className="rounded-md h-9 text-[13px] border-slate-200 focus-visible:ring-1 focus-visible:ring-[#0F172A]"
              />
              <div className="text-[11px] text-slate-500">2–48 chars · lowercase letters, digits, hyphens. Leave blank to auto-generate.</div>
            </div>
          </div>

          {/* Footer — sharp, navy CTA */}
          <DialogFooter className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 sm:justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-md text-slate-600 hover:bg-white hover:text-slate-900 h-9 px-4 text-[13px]">Cancel</Button>
            <Button onClick={create} disabled={creating || !tplId} className="rounded-md bg-[#0F172A] hover:bg-[#1e293b] text-white h-9 px-5 text-[13px] font-medium" data-testid="lander-create-submit">
              {creating ? <><Loader2 className="size-3.5 animate-spin" /> Creating…</> : "Create lander"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Mini({ label, value, Icon }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-slate-500">
        <Icon className="size-3.5" /> {label}
      </div>
      <div className="text-[18px] font-semibold tabular-nums text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function EmptyState({ onClick }) {
  return (
    <Card className="rounded-xl border-dashed border-slate-300 bg-slate-50/40">
      <CardContent className="p-10 text-center space-y-3">
        <div className="size-12 rounded-full bg-[#0055FF]/10 text-[#0055FF] grid place-items-center mx-auto">
          <Sparkles className="size-5" />
        </div>
        <h3 className="font-medium text-slate-900">Spin up your first lander.</h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto">Pick a sector template, customise the copy + brand colour, and you're live at a hosted URL in under 60 seconds. Every submission flows straight into your ingest.</p>
        <Button onClick={onClick} className="mt-3 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="lander-empty-cta"><Plus className="size-4" /> New lander</Button>
      </CardContent>
    </Card>
  );
}
